// Helpers
import { pushUniqueValue, removeListValue } from "@/Common/helpers/Arrays";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";

// Types
import { TMapErrorsOrNull } from "./types";

// Interfaces
import { IErrorAction } from "./interfaces";

const isKeyInErrors = (errors: string[] = [], key = "") => errors.includes(key);

const manageErrors = (errors: string[] = [], key = "", strategy = "add"): string[] => {
	const isKeyIncluded = isKeyInErrors(errors, key);
	if ((isKeyIncluded && strategy === "add") || (!isKeyIncluded && strategy === "delete")) {
		return [...errors];
	}

	let updatedErrors = [];
	if (strategy === "add") {
		updatedErrors = pushUniqueValue(errors, key);
	} else {
		updatedErrors = removeListValue(errors, key);
	}
	return [...updatedErrors];
};

const addError = (errors: string[] = [], key = ""): string[] => {
	return manageErrors(errors, key);
};

const addErrors = (errors: string[] = [], errorsToAdd: string[] = []) => {
	if (errorsToAdd.length) {
		const result = [...errors, ...errorsToAdd];
		return [...new Set(result)];
	}
	return errors;
};

const removeError = (errors: string[] = [], key = "") => {
	return manageErrors(errors, key, "delete");
};

const removeErrors = (errors: string[] = [], errorsToRemove: string[] = []) => {
	if (errorsToRemove.length) {
		return errors.filter((error) => !errorsToRemove.includes(error));
	}
	return errors;
};

const setState = (state: Map<string, string[]>) => {
	return new Map(state);
};

const isSameStateList = (current: string[], prev: string[]): boolean => current?.length === prev?.length;
const isStrictRemove = (current: string[], prev: string[], type: string) => isSameStateList(current, prev) && type === "REMOVE";

const addErrorsHandler = (prevErrors: string[], errorKey: string, newErrors: string[], type = "ADD"): string[] | null => {
	let errors = [];
	if (type === "ADD") {
		errors = addError([...prevErrors], errorKey);
	} else {
		errors = addErrors([...prevErrors], newErrors);
	}

	if (isSameStateList(errors, prevErrors)) {
		return null;
	}

	return errors;
};

const deleteErrorsHandler = (prevErrors: string[], errorKey: string, newErrors: string[], type = "REMOVE"): string[] | null => {
	let errors = [];
	if (type === "REMOVE") {
		errors = removeError([...prevErrors], errorKey);
	} else {
		errors = removeErrors([...prevErrors], newErrors);
	}

	if (isStrictRemove(errors, [...prevErrors], type)) {
		return null;
	}

	return errors;
};

const getPreviousErrors = (state: Map<string, string[]>, key = "") => {
	const previous = new Map(state);
	return previous.get(key) || [];
};

const getComplementaryErrors = (errors: string[] | null | undefined) => {
	return errors || [];
};

const getUpdatedState = (state: Map<string, string[]>, action: IErrorAction, errorKey: string, type: string): TMapErrorsOrNull => {
	const prevErrors = getPreviousErrors(state, action.key);
	const complementalErrors = getComplementaryErrors(action?.errors);

	switch (type) {
		case "ADD":
		case "ADD_LIST_ERRORS": {
			const errors = addErrorsHandler(prevErrors, errorKey, complementalErrors, type);
			if (errors) {
				return state.set(action.key, errors);
			}
			break;
		}
		case "REMOVE":
		case "REMOVE_LIST_ERRORS": {
			const errors = deleteErrorsHandler(prevErrors, errorKey, complementalErrors, type);
			if (!errors) {
				break;
			}

			if (!errors?.length) {
				state.delete(action.key);
				return state;
			}

			return state.set(action.key, errors);
		}
		default:
			break;
	}
	return null;
};

const handleErrorsActions = (state: Map<string, string[]>, action: IErrorAction) => {
	const { type } = action;
	const updatedState = setState(state);

	if (type === "CLEAR") {
		return new Map();
	}

	const errorKey = getErrorKey(action.key, action?.tabKey);
	if (errorKey === "_error") {
		return state;
	}

	if (type === "CLEAR_LIST_ERRORS") {
		if (state.has(action.key)) {
			updatedState.delete(action.key);
			return updatedState;
		}
	} else {
		const updated = getUpdatedState(updatedState, action, errorKey, type);
		if (updated) {
			return updated;
		}
	}

	return state;
};

export { handleErrorsActions };
